import { computed, ref } from "vue";
import { defineStore, storeToRefs } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { useMCampaignStore } from "@stores/mCampaign";
import { useMAudiencesStore } from "@stores/mAudiences";
import { ageOptions } from "@utils/ageRanges";
import generateToken from "@utils/generateToken";
import { useLocalesStore } from "@stores/mLocales";

export const useMAudienceStore = defineStore("mAudience", () => {
  const mAudiencesStore = useMAudiencesStore();
  const { _selectedAudience } = storeToRefs(mAudiencesStore);
  const mCampaignStore = useMCampaignStore();
  const { getCampaignAdAccount } = storeToRefs(mCampaignStore);
  const mLocalesStore = useLocalesStore();
  const { locales } = storeToRefs(mLocalesStore);

  // general
  const getMAudience = computed(() => _selectedAudience.value || {});
  const getMAudienceName = computed(() => getMAudience.value.name);
  const getMAudienceLocations = computed(() => getMAudience.value.t_geo?.locations);
  const getAgeOptions = computed(() => ageOptions);
  const isGenderSegmentationActive = computed(() => getMAudience.value.segment_by_gender);
  const getMAudienceGenderSegments = computed(
    () => getMAudience.value.m_dimensions?.find((d) => d.dimension_type === "gender")?.m_segments,
  );

  const createDefaultTLocales = () => (getMAudience.value.t_locales = []);
  const getMAudienceLocales = computed(() => getMAudience.value.t_locales ?? createDefaultTLocales());

  const updateMAudienceChannel = (channel, value) => (getMAudience.value.channels[channel] = value);
  const updateMAudienceTGender = (gender, value) => (getMAudience.value.t_gender[gender] = value);

  // age brackets
  const ageSegmentOptions = ref([
    { value: 1, label: "18/35/55" },
    { value: 2, label: "18/25/35/45/55/65" },
    { value: 3, label: "18/30/45/60" },
    { value: 4, label: "18/19/20...25" },
  ]);

  const preparedAgeSegments = ref({
    1: [
      [18, 34],
      [35, 54],
      [55, 65],
    ],
    2: [
      [18, 24],
      [25, 34],
      [35, 44],
      [45, 54],
      [55, 64],
      [65, 65],
    ],
    3: [
      [18, 29],
      [30, 44],
      [45, 59],
      [60, 65],
    ],
    4: Array.from({ length: 8 }, (_, i) => [18 + i, 18 + i]),
  });
  const getMAudienceAgeBrackets = computed(() => getMAudience.value.age_brackets ?? []);
  const canRemoveAgeBracket = computed(() => getMAudienceAgeBrackets.value.length > 1);
  const canStopAgeSplit = computed(
    () => getMAudience.value.segment_by_age === true && getMAudienceAgeBrackets.value.length === 1,
  );

  const setAgeBrackets = (brackets) => (getMAudience.value.age_brackets = brackets);
  const applyAgePreset = (preset) =>
    setAgeBrackets(
      preparedAgeSegments.value[preset].map(([min, max], index) => ({
        token: getMAudienceAgeBrackets.value[index]?.token ?? null,
        min,
        max,
      })),
    );
  const addAgeBracket = () => setAgeBrackets([...getMAudienceAgeBrackets.value, { token: null, min: 18, max: 65 }]);
  const removeAgeBracket = (index) => {
    if (!canRemoveAgeBracket.value) return;
    setAgeBrackets(getMAudienceAgeBrackets.value.filter((_, bracketIndex) => bracketIndex !== index));
  };
  const updateAgeBracket = (index, key, value) => (getMAudienceAgeBrackets.value[index][key] = Number(value));
  const stopAgeSplit = () => (getMAudience.value.segment_by_age = false);

  // interests
  const interests = ref([]);
  const suggestedInterests = ref([]);
  const getMAudienceInterests = computed(() => getMAudience.value.t_interests);
  const getMAudienceInterestIds = computed(() =>
    (getMAudienceInterests.value || []).map((interest) => String(interest.id)),
  );
  const getInterestOptions = computed(() =>
    interests.value?.map((interest) => {
      return { label: interest.name, value: interest.id, type: interest.type };
    }),
  );
  const getSuggestionOptions = computed(() =>
    suggestedInterests.value?.map((interest) => {
      return { label: interest.name, value: interest.id, type: interest.type };
    }),
  );
  const initMAudienceInterests = () => (getMAudience.value.t_interests = []);
  const removeAllInterests = () => (getMAudience.value.t_interests = []);
  const removeInterest = (index) => getMAudience.value.t_interests.splice(index, 1);

  const findInterestOption = (id) =>
    [...(interests.value || []), ...(suggestedInterests.value || [])].find((interest) => String(interest.id) === id);

  const updateMAudienceInterests = (nextIds) => {
    if (!getMAudienceInterests.value) initMAudienceInterests();
    const currentIds = getMAudienceInterestIds.value;
    const nextIdSet = new Set(nextIds.map(String));
    const selectedInterests = getMAudience.value.t_interests;

    const keptInterests = selectedInterests.filter((interest) => nextIdSet.has(String(interest.id)));
    const addedInterests = [...nextIdSet]
      .filter((id) => !currentIds.includes(id))
      .map(findInterestOption)
      .filter(Boolean);
    selectedInterests.splice(0, selectedInterests.length, ...keptInterests, ...addedInterests);
  };

  const handleInterests = (interest) => {
    return {
      ...interest,
      external_id: interest?.id || "UNKNOWN",
      size: interest?.audience_size || 0,
      active: true,
      source: "FACEBOOK",
      channel: "Facebook",
      levels: ["Facebook", ...(interest?.path || [])].filter((level) => level !== interest.name),
      token: generateToken(),
      target_type: interest?.type || "UNKNOWN",
    };
  };

  let latestInterestSearch = "";

  const updateInterestOptions = async (search) => {
    latestInterestSearch = search;
    if (search.length < 1) {
      interests.value = [];
      return true;
    }

    try {
      const url = `/facebook/${getCampaignAdAccount.value}/targetingsearch?q=${encodeURIComponent(search)}`;
      const { response, data } = await useMyFetch(url).json();
      if (search !== latestInterestSearch) return false;

      if (response.value.ok) interests.value = data.value?.data.map(handleInterests);
    } catch (err) {
      handleError(err);
    }
    return true;
  };

  const fetchInterestsSuggestions = async () => {
    try {
      const currentInterests = JSON.stringify(
        getMAudienceInterests.value?.map((interest) => {
          return { type: interest.type, id: interest.id };
        }),
      );
      const url = `/facebook/${getCampaignAdAccount.value}/targetingsuggestions?targeting_list=${encodeURIComponent(currentInterests)}`;
      const { response, data } = await useMyFetch(url).json();

      if (response.value.ok) suggestedInterests.value = data.value?.data?.map(handleInterests);
    } catch (err) {
      handleError(err);
    }
  };

  // Locales
  const removeAllLocales = () => {
    getMAudience.value.t_locales = [];
  };

  const removeLocale = (locale) => {
    const AuxArray = getMAudienceLocales.value.filter((t) => t !== locale);
    getMAudience.value.t_locales = AuxArray;
  };

  const addLocale = (value) => {
    const localesObj = locales.value.find((locale) => locale.key == value);
    if (localesObj) getMAudience.value.t_locales = [...getMAudienceLocales.value, localesObj];
  };

  const isLocaleSelected = (key) => getMAudienceLocales.value?.find((l) => l.key === key) != null;

  return {
    // general
    getMAudience,
    getMAudienceName,
    getMAudienceLocations,
    getAgeOptions,
    isGenderSegmentationActive,
    getMAudienceGenderSegments,
    updateMAudienceChannel,
    updateMAudienceTGender,

    // age brackets
    getMAudienceAgeBrackets,
    canRemoveAgeBracket,
    canStopAgeSplit,
    preparedAgeSegments,
    ageSegmentOptions,
    applyAgePreset,
    addAgeBracket,
    removeAgeBracket,
    updateAgeBracket,
    stopAgeSplit,

    // interests
    getMAudienceInterests,
    getMAudienceInterestIds,
    getInterestOptions,
    getSuggestionOptions,
    removeAllInterests,
    removeInterest,
    updateInterestOptions,
    updateMAudienceInterests,
    fetchInterestsSuggestions,

    // Locales
    getMAudienceLocales,
    removeAllLocales,
    removeLocale,
    addLocale,
    isLocaleSelected,
  };
});
